import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, Loader2, Award, Check, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("kcis_admin_token");
  return { Authorization: `Bearer ${t}` };
}

const STATUS_PILL = {
  pending_admin_approval: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  denied: "bg-rose-100 text-rose-800",
  issued: "bg-purple-100 text-purple-800",
};

export default function AdminKuhasCertificates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending_admin_approval");
  const [busy, setBusy] = useState({});
  const [noteById, setNoteById] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const url = new URL(`${API}/api/admin/kuhas/certificate-requests`);
      if (status) url.searchParams.set("status", status);
      const r = await fetch(url.toString(), { headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setItems((await r.json()).items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const decide = async (id, action) => {
    setBusy((b) => ({ ...b, [id]: action }));
    try {
      const r = await fetch(
        `${API}/api/admin/kuhas/certificate-requests/${id}/${action}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ note: noteById[id] || null }),
        }
      );
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail?.message || body?.detail || `HTTP ${r.status}`);
      toast.success(`Certificate ${body.status}`);
      load();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  const setNote = (id, val) => setNoteById((n) => ({ ...n, [id]: val }));

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-xs text-purple-900 hover:underline flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Admin hub
            </Link>
            <span className="font-display text-lg text-slate-900">KUHAS · Certificate Approvals</span>
          </div>
          <button onClick={load} disabled={loading}
                  className="text-xs text-slate-700 hover:text-slate-900 flex items-center gap-1">
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-4" data-testid="admin-kuhas-certificates">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Certificate Requests</h1>
          <p className="mt-1 text-sm text-slate-600">
            Every graduation event queues a request here. Approval is required before
            a credential is officially issued. The alumnus is not blocked from opting in
            to the public directory while a request is pending.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="text-slate-500">Filter:</span>
          {["pending_admin_approval", "approved", "denied", "issued", ""].map((s) => (
            <button key={s || "all"} onClick={() => setStatus(s)}
                    className={`px-2.5 h-7 rounded-full ${
                      status === s ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"
                    }`}
                    data-testid={`cert-filter-${s || "all"}`}>
              {(s || "all").replace(/_/g, " ")}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">
            No certificate requests match this filter.
          </div>
        ) : (
          items.map((c) => (
            <article key={c.id} className="rounded-2xl border border-slate-200 bg-white p-5"
                      data-testid={`cert-row-${c.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-purple-900" />
                    <span className="font-mono text-sm text-slate-900">{c.student_email}</span>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${STATUS_PILL[c.status] || "bg-slate-100"}`}>
                      {c.status.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-slate-700 flex flex-wrap gap-x-4 gap-y-1">
                    <span>program: <b>{c.program_slug}</b></span>
                    <span>credential: <b>{c.credential_type}</b></span>
                    <span className="text-slate-500">
                      requested {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                </div>
                {c.status === "pending_admin_approval" && (
                  <div className="flex flex-col gap-2 flex-shrink-0 w-64">
                    <input
                      value={noteById[c.id] || ""}
                      onChange={(e) => setNote(c.id, e.target.value)}
                      className="rounded-lg border border-slate-200 px-2.5 h-8 text-xs"
                      placeholder="Note (optional)"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => decide(c.id, "approve")}
                        disabled={!!busy[c.id]}
                        className="flex-1 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs h-8 inline-flex items-center justify-center gap-1"
                        data-testid={`cert-approve-${c.id}`}
                      >
                        {busy[c.id] === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Approve
                      </button>
                      <button
                        onClick={() => decide(c.id, "deny")}
                        disabled={!!busy[c.id]}
                        className="flex-1 rounded-lg bg-rose-700 hover:bg-rose-800 text-white text-xs h-8 inline-flex items-center justify-center gap-1"
                        data-testid={`cert-deny-${c.id}`}
                      >
                        {busy[c.id] === "deny" ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                        Deny
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </article>
          ))
        )}
      </main>
    </div>
  );
}
